import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Globe, Heart, Battery, Leaf } from 'lucide-react';

const CleanBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
      {/* Subtle geometric patterns */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-blue-400 rounded-full"></div>
        <div className="absolute top-1/3 right-1/3 w-24 h-24 bg-green-400 transform rotate-45"></div>
        <div className="absolute bottom-1/3 left-1/2 w-28 h-28 bg-purple-400 rounded-full"></div>
      </div>
    </div>
  );
};

export default function About() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Stats data - can be used for future stats section
  // const stats = [
  //   { label: "YEARS_EXPERIENCE", value: "5+", icon: Award },
  //   { label: "PROJECTS_COMPLETED", value: "25+", icon: Target },
  //   { label: "INVESTORS_SERVED", value: "1.2K+", icon: Users },
  //   { label: "CARBON_REDUCED", value: "50K kg", icon: Leaf },
  // ];

  const values = [
    {
      icon: Shield,
      title: "TRANSPARENCY",
      description: "Every transaction is recorded on the blockchain, ensuring complete transparency and accountability in all our operations.",
      color: "var(--cyber-blue)"
    },
    {
      icon: Leaf,
      title: "SUSTAINABILITY",
      description: "We prioritize environmental sustainability, focusing exclusively on renewable energy projects that reduce carbon footprint.",
      color: "var(--eco-green)"
    },
    {
      icon: Zap,
      title: "INNOVATION",
      description: "Leveraging cutting-edge blockchain technology to revolutionize how clean energy investments are made and managed.",
      color: "var(--neon-green)"
    },
    {
      icon: Heart,
      title: "COMMUNITY",
      description: "Building a global community of investors committed to creating a sustainable future for generations to come.",
      color: "var(--electric-purple)"
    }
  ];

  return (
    <div className="min-h-screen relative bg-gray-50">
      <CleanBackground />

      <div className="relative z-10 container mx-auto px-4 py-20">
        {/* Page Title */}
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            variants={itemVariants}
          >
            About GreenOVA8
          </motion.h1>
          <motion.p
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            variants={itemVariants}
          >
            Revolutionizing clean energy investment through blockchain technology
          </motion.p>
        </motion.div>

        {/* Hero Image Section */}
        <motion.div
          className="mb-20"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="/assets/images/renewable-energy-infrastructure.jpg"
              alt="Renewable Energy Infrastructure"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-2xl font-bold mb-2">Building the Future of Energy</h3>
              <p className="text-lg opacity-90">Advanced renewable energy infrastructure powering sustainable communities</p>
            </div>
          </div>
        </motion.div>

        {/* Mission Section */}
        <motion.div
          className="bg-white rounded-2xl p-8 md:p-12 mb-20 shadow-lg border border-gray-200"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg leading-relaxed mb-6 text-gray-600">
                GreenOVA8 was founded with a singular vision: to democratize access to renewable energy investments
                while accelerating the global transition to clean energy through innovative blockchain technology.
              </p>
              <p className="text-lg leading-relaxed text-gray-600">
                We believe that by combining the transparency of blockchain with the urgency of climate action,
                we can create unprecedented opportunities for both investors and the planet.
              </p>
              <div className="mt-8 flex space-x-4">
                <div className="status-badge status-active">
                  <Globe className="w-3 h-3" />
                  GLOBAL_IMPACT
                </div>
                <div className="status-badge status-online">
                  <Battery className="w-3 h-3" />
                  RENEWABLE_FOCUS
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Technology Info Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-6 rounded-xl text-center border border-green-200">
                  <div className="text-sm font-medium mb-2 text-green-600">BLOCKCHAIN</div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">SOLANA</div>
                  <div className="text-xs text-gray-600">NETWORK</div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl text-center border border-blue-200">
                  <div className="text-sm font-medium mb-2 text-blue-600">SMART</div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">CONTRACTS</div>
                  <div className="text-xs text-gray-600">AUDITED</div>
                </div>
              </div>

              {/* Technology Image */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <img
                  src="/assets/images/clean-energy-technology.jpg"
                  alt="Clean Energy Technology"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Advanced Technology</h3>
                <p className="text-sm text-gray-600">Cutting-edge renewable energy solutions powered by blockchain</p>
              </div>

              {/* Dashboard Metrics */}
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--neon-green)' }}></div>
                    <span className="text-sm font-mono">TOTAL_INVESTED</span>
                  </div>
                  <span className="text-sm font-bold text-white">$2.5M</span>
                </div>

                <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--cyber-blue)' }}></div>
                    <span className="text-sm font-mono">ACTIVE_PROJECTS</span>
                  </div>
                  <span className="text-sm font-bold text-white">25+</span>
                </div>

                <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--electric-purple)' }}></div>
                    <span className="text-sm font-mono">AVG_ROI</span>
                  </div>
                  <span className="text-sm font-bold text-white">+26.2%</span>
                </div>

                <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--eco-green)' }}></div>
                    <span className="text-sm font-mono">CO₂_SAVED</span>
                  </div>
                  <span className="text-sm font-bold text-white">50K kg</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs mb-2">
                  <span style={{ color: 'var(--secondary-text)' }}>Portfolio Growth</span>
                  <span style={{ color: 'var(--neon-green)' }}>+12.5%</span>
                </div>
                <div className="w-full glass-card rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-1000"
                    style={{
                      background: 'linear-gradient(90deg, var(--neon-green), var(--cyber-blue))',
                      width: '75%'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold font-mono text-center mb-16 holo-text">
            OUR_<span style={{ color: 'var(--neon-green)' }}>VALUES</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  className="glass-card p-6 text-center relative overflow-hidden group"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 + index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="relative z-10">
                    <div
                      className="w-16 h-16 mx-auto mb-4 glass-card flex items-center justify-center"
                      style={{ boxShadow: `0 0 20px ${value.color}` }}
                    >
                      <Icon className="w-8 h-8" style={{ color: value.color }} />
                    </div>
                    <h3 className="text-xl font-bold font-mono mb-4" style={{ color: 'var(--primary-text)' }}>
                      {value.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--secondary-text)' }}>
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Renewable Energy Technologies Section */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold font-mono text-center mb-16 holo-text">
            RENEWABLE_<span style={{ color: 'var(--neon-green)' }}>TECHNOLOGIES</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Solar Technology */}
            <motion.div
              className="glass-card p-6 text-center relative overflow-hidden"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <img
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&auto=format&fit=crop"
                alt="Solar Panel Array"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold font-mono mb-2" style={{ color: 'var(--neon-green)' }}>
                SOLAR_ENERGY
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--secondary-text)' }}>
                Advanced photovoltaic systems with 85% efficiency
              </p>

              {/* Fintech Metrics */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="glass-card p-2 rounded">
                  <div className="text-green-400 font-mono">ROI</div>
                  <div className="text-white font-bold">24.5%</div>
                </div>
                <div className="glass-card p-2 rounded">
                  <div className="text-blue-400 font-mono">CAPACITY</div>
                  <div className="text-white font-bold">2.4MW</div>
                </div>
              </div>
            </motion.div>

            {/* Wind Technology */}
            <motion.div
              className="glass-card p-6 text-center relative overflow-hidden"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <img
                src="https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=800&auto=format&fit=crop"
                alt="Wind Turbine Farm"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold font-mono mb-2" style={{ color: 'var(--cyber-blue)' }}>
                WIND_ENERGY
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--secondary-text)' }}>
                Smart wind turbines with real-time optimization
              </p>

              {/* Fintech Metrics */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="glass-card p-2 rounded">
                  <div className="text-blue-400 font-mono">ROI</div>
                  <div className="text-white font-bold">26.8%</div>
                </div>
                <div className="glass-card p-2 rounded">
                  <div className="text-purple-400 font-mono">CAPACITY</div>
                  <div className="text-white font-bold">1.8MW</div>
                </div>
              </div>
            </motion.div>

            {/* Battery Storage */}
            <motion.div
              className="glass-card p-6 text-center relative overflow-hidden"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <img
                src="https://images.unsplash.com/photo-1614935151651-0bea6508db6b?w=800&auto=format&fit=crop"
                alt="Battery Storage System"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-xl font-bold font-mono mb-2" style={{ color: 'var(--electric-purple)' }}>
                ENERGY_STORAGE
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--secondary-text)' }}>
                Advanced battery systems with 95% efficiency
              </p>

              {/* Fintech Metrics */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="glass-card p-2 rounded">
                  <div className="text-purple-400 font-mono">ROI</div>
                  <div className="text-white font-bold">28.2%</div>
                </div>
                <div className="glass-card p-2 rounded">
                  <div className="text-yellow-400 font-mono">CAPACITY</div>
                  <div className="text-white font-bold">500kWh</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Technology Section */}
        <motion.div
          className="glass-card p-8 md:p-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold font-mono mb-8 holo-text">
            POWERED_BY_<span style={{ color: 'var(--neon-green)' }}>SOLANA</span>
          </h2>

          <div className="max-w-4xl mx-auto">
            <p className="text-lg leading-relaxed mb-8" style={{ color: 'var(--secondary-text)' }}>
              Built on the Solana blockchain, GreenOVA8 leverages high-speed, low-cost transactions to make
              renewable energy investments accessible to everyone. Our smart contracts ensure transparent,
              secure, and automated execution of all investment processes.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 glass-card flex items-center justify-center mx-auto mb-4 glow-green">
                  <Zap className="w-8 h-8" style={{ color: 'var(--neon-green)' }} />
                </div>
                <h3 className="text-xl font-bold font-mono mb-2" style={{ color: 'var(--primary-text)' }}>
                  HIGH_SPEED
                </h3>
                <p className="text-sm" style={{ color: 'var(--secondary-text)' }}>
                  Sub-second transaction finality
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 glass-card flex items-center justify-center mx-auto mb-4" style={{ boxShadow: '0 0 20px var(--cyber-blue)' }}>
                  <Shield className="w-8 h-8" style={{ color: 'var(--cyber-blue)' }} />
                </div>
                <h3 className="text-xl font-bold font-mono mb-2" style={{ color: 'var(--primary-text)' }}>
                  SECURE
                </h3>
                <p className="text-sm" style={{ color: 'var(--secondary-text)' }}>
                  Military-grade encryption
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 glass-card flex items-center justify-center mx-auto mb-4" style={{ boxShadow: '0 0 20px var(--eco-green)' }}>
                  <Leaf className="w-8 h-8" style={{ color: 'var(--eco-green)' }} />
                </div>
                <h3 className="text-xl font-bold font-mono mb-2" style={{ color: 'var(--primary-text)' }}>
                  ECO_FRIENDLY
                </h3>
                <p className="text-sm" style={{ color: 'var(--secondary-text)' }}>
                  Energy-efficient consensus
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
